<template>
	<div>
		<div class="mt-3 w-full flex justify-center">
			<button @click="isModalOpen = true">
				Завантажити графік <FontAwesomeIcon :icon="faDownload" />
			</button>
		</div>

		<ModalComponent :is-open="isModalOpen" @modal-close="isModalOpen = false">
			<template #header>
				Експортувати графік
			</template>
			<template #content>
				<div class="flex flex-row gap-2 flex-wrap">
					<div class="shrink-0">
						<div class="text-sm font-medium mb-1">
							Тема
						</div>
						<div class="flex gap-1">
							<button @click="theme = 'light'" :selected="theme == 'light' ? true : null">
								Світла
								<FontAwesomeIcon :icon="faSun"/>
							</button>
							<button @click="theme = 'dark'" :selected="theme == 'dark' ? true : null">
								Темна
								<FontAwesomeIcon :icon="faMoon"/>
							</button>
						</div>
					</div>
					<div class="shrink-0 hidden">
						<div class="text-sm font-medium mb-1">
							Орієнтація
						</div>
						<div class="flex gap-1">
							<button @click="vertical = false">
								Горизонтальна
								<FontAwesomeIcon :icon="faDesktop"/>
							</button>
							<button @click="vertical = true">
								Вертикальна
								<FontAwesomeIcon :icon="faMobileScreenButton"/>
							</button>
						</div>
					</div>
				</div>

				<br>

				<PlotlyChart 
					class="rounded-md border border-border overflow-hidden relative imageAnimation"
					:data=plotlyData 
					:layout=finalLayout 
					:static-plot="true" 
					:fixed-range="true"
					:plot-width="size.width"
					:plot-height="size.height"
					:theme="theme"
					@image-ready="e => imageDataUrl = e"
				/>

				<br>

				<div class="flex gap-1">
					<button @click="downloadImage">
						<span>Завантажити</span>
						<FontAwesomeIcon :icon="faDownload" />
					</button>

					<button @click="copyImage" class="[&>span]:hidden sm:[&>span]:block">
						<span>Копіювати</span>
						<FontAwesomeIcon :icon="faCopy" />
					</button>
	
					<button @click="shareImage" class="[&>span]:hidden sm:[&>span]:block">
						<span>Поширити</span>
						<FontAwesomeIcon :icon="faShare" />
					</button>
				</div>
			</template>
		</ModalComponent>
	</div>
</template>

<script setup>
import { ref, defineProps, computed } from 'vue'
import ModalComponent from '@/components/ui/ModalComponent.vue'
import PlotlyChart from '@/components/charts/PlotlyChart.vue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSun, faMoon, faMobileScreenButton, faDesktop, faDownload, faShare, faCopy } from '@fortawesome/free-solid-svg-icons'

const props = defineProps({ 
	plotlyData: Object, 
	plotlyLayout: Object, 
	title: String, 
	subtitle: String 
})

const isModalOpen = ref(false)

const theme = ref("light")
const vertical = ref(false)

const imageDataUrl = ref(null)

const size = computed(() => {
	const long = 1280
	const short = 800
	return { 
		width: vertical.value ? short : long,
		height: vertical.value ? long : short,
	}
})

const finalLayout = computed(() => {
	return {
		...props.plotlyLayout,
		// margin: { t: 90, b: 35, l: 52, r: 5, pad: 0 },
		margin: null,
		title: {
			x: 0.045,
			y: 0.92,
			text: props.title,
			font: {
				family: 'Helvetica, Arial, serif',
				size: 18,
				weight: 800,
			},
			subtitle: {
				text: props.subtitle,
				font: {
					family: 'Helvetica, Arial, serif',
					size: 12
				},
			}
		},
		annotations: [
			{
				text: 'eco-city.org.ua',
				showarrow: false,
				xref: 'paper',
				yref: 'paper',
				x: 0.5,
				y: -0.2,
				xanchor: 'center',
				yanchor: 'top',
				font: {
					size: 12
				}
			}
		],
		// images: [
		// 	{
		// 		source: "/logo-white.svg",
		// 		visible: false,
		// 		layer: "below",
		// 		xanchor: "right",
		// 		yanchor: "bottom",
		// 		sizex: 0.5,
		// 		sizey: 0.5,
		// 		sizing: "contain",
		// 		opacity: theme.value == 'dark' ? 0.04 : 0.4,
		// 		xref: "paper",
		// 		x: 1,
		// 		yref: "paper",
		// 		y: 0.5,
		// 	}
		// ],
		// showlegend: true
	}
})

function downloadImage() {
	const a = document.createElement('a')
	a.href = imageDataUrl.value
	a.download = `${props.title} ${new Date().toISOString()}.png`
	document.body.appendChild(a)
	a.click()
	document.body.removeChild(a)
}

async function shareImage() {
	const blob = await fetch(imageDataUrl.value).then(res => res.blob());
	const file = new File([blob], `EcoCity_${new Date().toISOString().replace(":", "_")}.png`, blob)

	console.warn(file)

	const data = {
		url: window.location.href,
		text: props.title,
		title: props.subtitle.replace('<br>', '\n'),
		files: [file]
	}

	console.log(navigator.canShare(data))

	navigator.share(data)
}

async function copyImage() {
	const blob = await fetch(imageDataUrl.value).then(res => res.blob());

	const item = new ClipboardItem({ [blob.type]: blob });

	try {
		await navigator.clipboard.write([item]);
		alert('Зображення скопійовано до буфера обміну!');
	} catch (err) {
		console.error(err);
		alert('Не вдалося скопіювати зображення.');
	}
}
</script>

<style scoped>
.imageAnimation::after {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	transform: translateX(-120%);
	background-image: linear-gradient(90deg, #fff0 0, #fffa 40%, #fff2 60%, #fff0);
	animation: shimmer 2s ease-out;
	content: "";

	user-select: none;
}

.dark .imageAnimation::after {
	background-image: linear-gradient(90deg, #fff0 0, #aaa4 40%, #aaa1 60%, #fff0);
}

@keyframes shimmer {
	100% {
		transform: translateX(100%);
	}
}
</style>